import { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import type {
  ApiResponse,
  Project,
  ProjectCustomDomainSessionOption,
  ProjectShareInfo,
} from '@claude-code-api/shared';
import {
  Button,
  Card,
  Checkbox,
  Input,
  Label,
} from '@/components/ui';
import { ConfirmDialog } from '@/components/shared/ConfirmDialog';
import { ProjectCustomDomainCard } from './ProjectCustomDomainCard';
import { toast } from 'sonner';
import { Trash2, AlertTriangle, UserPlus, X } from 'lucide-react';

interface ProjectSettingsTabProps {
  project: Project;
  onProjectDeleted: () => void;
}

export function ProjectSettingsTab({ project, onProjectDeleted }: ProjectSettingsTabProps) {
  const { t } = useTranslation(['projects', 'common']);
  const [showDeleteConfirm, setShowDeleteConfirm] = useState(false);
  const [keepDatabaseOnDelete, setKeepDatabaseOnDelete] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Sharing state
  const [shares, setShares] = useState<ProjectShareInfo[]>([]);
  const [shareEmail, setShareEmail] = useState('');
  const [sharing, setSharing] = useState(false);
  const [loadingShares, setLoadingShares] = useState(true);
  const [showShareConfirm, setShowShareConfirm] = useState(false);
  const [sessions, setSessions] = useState<ProjectCustomDomainSessionOption[]>([]);

  useEffect(() => {
    void fetchShares();
    void fetchSessions();
  }, [project.id]);

  const fetchSessions = async () => {
    try {
      const response = await fetch(`/api/projects/${encodeURIComponent(project.id)}/custom-domain/sessions`, {
        credentials: 'include',
      });
      const result = await response.json() as ApiResponse<ProjectCustomDomainSessionOption[]>;
      if (!response.ok || !result.data) {
        throw new Error(result.error ?? t('projects:deployments.customDomain.errors.loadSessions'));
      }
      setSessions(result.data);
    } catch (error) {
      console.error('Failed to fetch sessions for custom domain settings:', error);
      toast.error(
        error instanceof Error
          ? error.message
          : t('projects:deployments.customDomain.errors.loadSessions')
      );
    }
  };

  const fetchShares = async () => {
    try {
      const response = await fetch(`/api/projects/${project.id}/shares`, {
        credentials: 'include',
      });
      if (response.ok) {
        const data = await response.json();
        setShares(data.data);
      }
    } catch (error) {
      console.error('Failed to fetch shares:', error);
    } finally {
      setLoadingShares(false);
    }
  };

  const handleShareSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    if (!shareEmail.trim()) return;
    setShowShareConfirm(true);
  };

  const addShare = async () => {
    setShowShareConfirm(false);
    setSharing(true);
    try {
      const response = await fetch(`/api/projects/${project.id}/shares`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        credentials: 'include',
        body: JSON.stringify({ email: shareEmail.trim() }),
      });

      const data = await response.json();
      if (response.ok) {
        setShares([data.data, ...shares]);
        setShareEmail('');
        toast.success(t('projects:settings.messages.shared'));
      } else {
        toast.error(data.error || t('projects:settings.errors.share'));
      }
    } catch (error) {
      console.error('Failed to share project:', error);
      toast.error(t('projects:settings.errors.share'));
    } finally {
      setSharing(false);
    }
  };

  const removeShare = async (shareId: string) => {
    try {
      const response = await fetch(`/api/projects/${project.id}/shares/${shareId}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        setShares(shares.filter(s => s.id !== shareId));
        toast.success(t('projects:settings.messages.shareRemoved'));
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('projects:settings.errors.removeShare'));
      }
    } catch (error) {
      console.error('Failed to remove share:', error);
      toast.error(t('projects:settings.errors.removeShare'));
    }
  };

  const deleteProject = async () => {
    setDeleting(true);
    try {
      const queryParams = keepDatabaseOnDelete ? '?keepDatabase=true' : '';
      const response = await fetch(`/api/projects/${project.id}${queryParams}`, {
        method: 'DELETE',
        credentials: 'include',
      });

      if (response.ok) {
        onProjectDeleted();
      } else {
        const data = await response.json().catch(() => ({}));
        toast.error(data.error || t('projects:settings.errors.deleteProject'));
      }
    } catch (error) {
      console.error('Failed to delete project:', error);
      toast.error(t('projects:settings.errors.deleteProject'));
    } finally {
      setDeleting(false);
    }
  };

  return (
    <div>
      <Card className="overflow-hidden">
        <div className="border-b">
          <ProjectCustomDomainCard projectId={project.id} projectName={project.name} sessions={sessions} embedded />
        </div>

        <section className="border-b p-6" aria-labelledby="project-sharing-title">
          <h3 id="project-sharing-title" className="text-lg font-semibold font-heading mb-1">
            {t('projects:settings.sharingTitle')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('projects:settings.sharingDescription')}
          </p>

          <form onSubmit={handleShareSubmit} className="mb-4 flex flex-col gap-3 sm:flex-row">
            <Input
              type="email"
              placeholder={t('projects:settings.emailPlaceholder')}
              value={shareEmail}
              onChange={(e) => setShareEmail(e.target.value)}
              className="flex-1"
              required
            />
            <Button type="submit" disabled={sharing || !shareEmail.trim()} className="w-full gap-2 sm:w-auto">
              <UserPlus className="w-4 h-4" />
              {sharing ? t('projects:settings.sharing') : t('projects:settings.share')}
            </Button>
          </form>

          {loadingShares ? (
            <p className="text-sm text-muted-foreground">{t('common:loading')}</p>
          ) : shares.length === 0 ? (
            <p className="text-sm text-muted-foreground">{t('projects:settings.notSharedYet')}</p>
          ) : (
            <div className="space-y-2">
              {shares.map((share) => (
                <div
                  key={share.id}
                  className="flex items-center justify-between p-3 border rounded-lg"
                >
                  <div className="min-w-0">
                    <p className="text-sm font-medium truncate">{share.userEmail}</p>
                    {share.userName && (
                      <p className="text-xs text-muted-foreground truncate">{share.userName}</p>
                    )}
                  </div>
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => removeShare(share.id)}
                    className="h-8 w-8 p-0 text-muted-foreground hover:text-destructive"
                    aria-label={t('projects:settings.removeShareAria', { email: share.userEmail })}
                  >
                    <X className="w-4 h-4" />
                  </Button>
                </div>
              ))}
            </div>
          )}
        </section>

        <section className="p-6" aria-labelledby="project-danger-title">
          <h3 id="project-danger-title" className="text-lg font-semibold font-heading mb-1 text-destructive">
            {t('projects:settings.dangerTitle')}
          </h3>
          <p className="text-sm text-muted-foreground mb-4">
            {t('projects:settings.dangerDescription')}
          </p>

          <div className="flex flex-col gap-4 py-2 sm:flex-row sm:items-center sm:justify-between">
            <div className="min-w-0">
              <p className="text-sm font-medium">{t('projects:settings.deleteThisProject')}</p>
              <p className="text-xs text-muted-foreground mt-1">
                {t('projects:settings.deleteThisProjectHelp')}
              </p>
            </div>
            <Button
              variant="destructive"
              className="w-full sm:w-auto"
              onClick={() => setShowDeleteConfirm(true)}
            >
              <Trash2 className="w-4 h-4 mr-2" />
              {t('projects:settings.deleteProject')}
            </Button>
          </div>
        </section>
      </Card>

      <ConfirmDialog
        open={showDeleteConfirm}
        onOpenChange={(open) => {
          setShowDeleteConfirm(open);
          if (!open) setKeepDatabaseOnDelete(false);
        }}
        title={t('projects:settings.deleteConfirmTitle')}
        description={t('projects:settings.deleteConfirmDescription', { name: project.name })}
        confirmLabel={t('projects:settings.deleteProject')}
        cancelLabel={t('common:cancel')}
        onConfirm={deleteProject}
        loading={deleting}
        variant="destructive"
      >
        <div className="py-2 space-y-4">
          {project.hasDatabase && (
            <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
              <div className="flex items-start gap-3">
                <Checkbox
                  id="keepDatabase"
                  checked={keepDatabaseOnDelete}
                  onCheckedChange={(checked) => setKeepDatabaseOnDelete(checked === true)}
                  className="mt-0.5"
                />
                <div>
                  <Label htmlFor="keepDatabase" className="text-sm font-medium cursor-pointer">
                    {t('projects:settings.keepDatabase')}
                  </Label>
                  <p className="text-xs text-muted-foreground mt-1">
                    {keepDatabaseOnDelete
                      ? t('projects:settings.keepDatabaseCheckedHelp')
                      : t('projects:settings.keepDatabaseHelp')}
                  </p>
                </div>
              </div>
            </div>
          )}

          <div className="bg-destructive/10 border border-destructive/20 rounded-xl p-4">
            <p className="text-sm text-destructive font-medium flex items-center gap-2">
              <AlertTriangle className="w-4 h-4" />
              {keepDatabaseOnDelete
                ? t('projects:settings.projectFilesDeleted')
                : t('projects:settings.allProjectDataDeleted')
              }
            </p>
          </div>
        </div>
      </ConfirmDialog>

      <ConfirmDialog
        open={showShareConfirm}
        onOpenChange={setShowShareConfirm}
        title={t('projects:settings.shareConfirmTitle')}
        description={t('projects:settings.shareConfirmDescription', { name: project.name, email: shareEmail.trim() })}
        confirmLabel={t('projects:settings.share')}
        cancelLabel={t('common:cancel')}
        onConfirm={addShare}
        loading={sharing}
        variant="default"
      >
        <div className="py-2">
          <div className="bg-primary/5 border border-primary/20 rounded-xl p-4">
            <p className="text-sm font-medium mb-1">{t('projects:settings.permissionsTitle')}</p>
            <ul className="text-sm text-muted-foreground space-y-1 list-disc list-inside">
              <li>{t('projects:settings.permissions.viewSessions')}</li>
              <li>{t('projects:settings.permissions.startSessions')}</li>
              <li>{t('projects:settings.permissions.accessDatabase')}</li>
              <li>{t('projects:settings.permissions.viewDeployments')}</li>
            </ul>
          </div>
        </div>
      </ConfirmDialog>
    </div>
  );
}
